import alertIcon from "@loot-os/icons/icons/alert";
import infoIcon from "@loot-os/icons/icons/info";
import successIcon from "@loot-os/icons/icons/success";
import warningIcon from "@loot-os/icons/icons/warning";
import type { IconData } from "@loot-os/icons/types";
import type { ReactNode } from "react";
import { cn, tv } from "../foundations/cn.js";
import { Icon } from "./Icon.js";

const alertStyles = tv({
  base: "flex items-start gap-2.5 rounded-xl border px-3.5 py-3 font-body text-body-b2",
  variants: {
    tone: {
      neutral: "border-border-subtle bg-bg-raised text-text-secondary",
      accent: "border-accent-default/40 bg-accent-subtle text-text-primary",
      success: "border-status-success/40 bg-status-success-subtle text-text-primary",
      warning: "border-status-warning/40 bg-status-warning-subtle text-text-primary",
      danger: "border-status-danger/40 bg-status-danger-subtle text-text-primary",
    },
  },
  defaultVariants: { tone: "neutral" },
});

const ICON_TONE = {
  neutral: "text-text-tertiary",
  accent: "text-accent-text",
  success: "text-status-success",
  warning: "text-status-warning",
  danger: "text-status-danger",
} as const;

const DEFAULT_ICON: Record<AlertTone, IconData> = {
  neutral: infoIcon,
  accent: infoIcon,
  success: successIcon,
  warning: warningIcon,
  danger: alertIcon,
};

export type AlertTone = keyof typeof ICON_TONE;

export interface AlertProps {
  /**
   * `danger` is announced at once (role=alert); the others politely (role=status). `accent`
   * draws attention to something on offer (signed out, opens are demos); `warning` to a condition
   * the shopper should know before going on.
   */
  tone?: AlertTone;
  /** A few strong words on their own line before the text: "This account is locked." */
  title?: ReactNode;
  icon?: IconData;
  /**
   * Whether a screen reader hears it when it appears. Leave it on for something that just
   * happened (a refused request); turn it off for a standing condition shown on load (an account
   * lock, a demo notice), which is read where it sits instead of interrupting.
   */
  announce?: boolean;
  /** One action at the end: beside the text from sm, under it on a phone. */
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
}

/**
 * A status message in place, inside a form, a panel or over a page: what failed, what checked
 * out, what to wait for, a standing condition. The words carry the meaning; the tone and icon
 * only echo them.
 */
export function Alert({
  tone = "neutral",
  title,
  icon,
  announce = true,
  action,
  children,
  className,
}: AlertProps) {
  const role = announce ? (tone === "danger" ? "alert" : "status") : undefined;
  return (
    <div role={role} className={alertStyles({ tone, className })}>
      <Icon icon={icon ?? DEFAULT_ICON[tone]} className={cn("mt-px shrink-0", ICON_TONE[tone])} />
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-0.5 break-words">
          {title && <p className="text-body-b2-strong text-text-primary">{title}</p>}
          {children}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}
